import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { LegalLinks } from "@/components/LegalLinks";
import { BackButton } from "@/components/BackButton";
import { BucketList } from "@/components/BucketList";
import { Itinerary } from "@/components/Itinerary";
import { MySaves } from "@/components/MySaves";
import { PackingLists } from "@/components/PackingLists";
import { MapPin, Plane, Bookmark, ListChecks, ChevronRight } from "lucide-react";

const SECTIONS = [
  { value: "bucket", label: "Bucket List", desc: "Dream destinations & experiences", icon: MapPin, testid: "tab-bucket-list", Comp: BucketList, c: "bg-rose-100 text-rose-500" },
  { value: "itinerary", label: "Itineraries", desc: "Day-by-day trip plans", icon: Plane, testid: "tab-itinerary", Comp: Itinerary, c: "bg-sky-100 text-sky-600" },
  { value: "saves", label: "My Saves", desc: "Restaurants, hotels, places", icon: Bookmark, testid: "tab-my-saves", Comp: MySaves, c: "bg-emerald-100 text-emerald-600" },
  { value: "packing", label: "Packing Lists", desc: "Checklists so nothing's left behind", icon: ListChecks, testid: "tab-packing-lists", Comp: PackingLists, c: "bg-amber-100 text-amber-600" },
];

// Daily-rotating full landscape backdrops (~40 — repeats only after ~40 days)
const B = "?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
const BP = "?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const BACKDROPS = [
  `https://images.unsplash.com/photo-1766699623503-f9a211323f96${B}`,
  `https://images.unsplash.com/photo-1551524164-7d2f9ff12c70${B}`,
  `https://images.unsplash.com/photo-1583599507487-eea43bae3604${B}`,
  `https://images.unsplash.com/photo-1710762635726-531b4a22d4ca${B}`,
  `https://images.unsplash.com/photo-1503327431567-3ab5e6e79140${B}`,
  `https://images.unsplash.com/photo-1779133375512-a8858513a15e${B}`,
  `https://images.unsplash.com/photo-1539667547529-84c607280d20${B}`,
  `https://images.unsplash.com/photo-1599600197870-4bfaf17d5f42${B}`,
  `https://images.unsplash.com/photo-1688541197205-02bd8c71074d${B}`,
  `https://images.unsplash.com/photo-1545556124-500dc7c01f2c${B}`,
  `https://images.unsplash.com/photo-1600582910964-5b7c109e6868${B}`,
  `https://images.unsplash.com/photo-1618064541372-289bdb6f5b7b${B}`,
  `https://images.unsplash.com/photo-1503381085584-e57c1c440b2d${B}`,
  `https://images.unsplash.com/photo-1596003903067-bf5762ad5c19${B}`,
  `https://images.unsplash.com/photo-1532274402911-5a369e4c4bb5${B}`,
  `https://images.unsplash.com/photo-1506953823976-52e1fdc0149a${BP}`,
  `https://images.unsplash.com/photo-1542401886-65d6c61db217${B}`,
  `https://images.unsplash.com/photo-1704365159747-1f7b8913044f${BP}`,
  `https://images.unsplash.com/photo-1617374128851-c84e37dc9f37${B}`,
  `https://images.unsplash.com/photo-1589312133722-eab40c686ebb${B}`,
  `https://images.unsplash.com/photo-1723058852872-0e7ecaf4055d${B}`,
  `https://images.unsplash.com/photo-1658051161493-1d311c4c7b4d${BP}`,
  `https://images.unsplash.com/photo-1680466257600-86e0aa02cc64${BP}`,
  `https://images.unsplash.com/photo-1664825381616-5cb8397fd9b1${B}`,
  `https://images.unsplash.com/photo-1611323340350-bdcc0e6cfae5${B}`,
  `https://images.unsplash.com/photo-1606516397986-1eeb79e8c052${B}`,
  `https://images.unsplash.com/photo-1614608042438-208e794750f0${BP}`,
  `https://images.unsplash.com/photo-1561571994-3c61c554181a${BP}`,
  `https://images.unsplash.com/photo-1578922746465-3a80a228f223${BP}`,
  `https://images.unsplash.com/photo-1531366936337-7c912a4589a7${B}`,
  `https://images.unsplash.com/photo-1464822759023-fed622ff2c3b${B}`,
  `https://images.unsplash.com/photo-1609488971595-a4c457ee4e12${BP}`,
  `https://images.unsplash.com/photo-1487009805257-5ed2eb9f10a3${B}`,
  `https://images.unsplash.com/photo-1578434223342-ce18f6da5683${B}`,
  `https://images.unsplash.com/photo-1594365458706-6fab3472f681${B}`,
  `https://images.unsplash.com/photo-1496588152823-86ff7695e68f${B}`,
  `https://images.unsplash.com/photo-1761990386038-31ff9e9bf70e${BP}`,
  `https://images.unsplash.com/photo-1633982341410-a6dbc348f9d5${B}`,
  `https://images.unsplash.com/photo-1759222973482-3c0d90dd8549${B}`,
  `https://images.unsplash.com/photo-1594800083755-8fe31b2c99df${BP}`,
];

// Accent themes that respond to each backdrop's mood
const THEMES = {
  teal: { ac: "174 72% 40%", hover: "174 72% 33%", soft: "173 60% 92%", softfg: "175 84% 25%" },
  sky: { ac: "199 89% 46%", hover: "200 90% 38%", soft: "204 94% 92%", softfg: "202 80% 30%" },
  indigo: { ac: "239 58% 58%", hover: "239 54% 48%", soft: "232 92% 94%", softfg: "239 55% 42%" },
  violet: { ac: "271 66% 56%", hover: "271 62% 46%", soft: "270 80% 95%", softfg: "272 60% 42%" },
  emerald: { ac: "160 82% 38%", hover: "160 82% 30%", soft: "152 68% 92%", softfg: "158 80% 24%" },
  amber: { ac: "30 90% 46%", hover: "28 90% 38%", soft: "43 96% 90%", softfg: "27 90% 33%" },
  coral: { ac: "9 82% 56%", hover: "9 78% 47%", soft: "11 95% 93%", softfg: "10 75% 40%" },
};

const THEME_BY_INDEX = [
  "teal", "teal", "amber", "sky", "coral", "teal", "teal", "coral",
  "teal", "teal", "teal", "teal", "emerald", "emerald", "emerald", "teal",
  "amber", "emerald", "amber", "emerald", "violet", "emerald", "indigo", "teal",
  "indigo", "sky", "emerald", "violet", "sky", "violet", "indigo", "emerald",
  "sky", "indigo", "coral", "indigo", "violet", "amber", "sky", "violet",
];

const dailyIndex = () => {
  const now = new Date();
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  return dayOfYear % BACKDROPS.length;
};

export default function Home() {
  const { user, refreshKey, accountEpoch, openLogin } = useAuth();
  const location = useLocation();
  const [active, setActive] = useState(SECTIONS.some((section) => section.value === location.state?.section) ? location.state.section : null);
  const [resumedSave, setResumedSave] = useState(location.state?.resumedSave || null);
  const firstName = user?.name?.split(" ")[0] || "Traveler";

  const [idx, setIdx] = useState(dailyIndex);
  const backdrop = BACKDROPS[idx];

  useEffect(() => {
    let timer;
    const sync = () => {
      setIdx(dailyIndex());
      clearTimeout(timer);
      const now = new Date();
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
      timer = setTimeout(sync, midnight - now);
    };
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    sync();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", sync);
    window.addEventListener("focus", sync);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  useEffect(() => {
    const t = THEMES[THEME_BY_INDEX[idx]] || THEMES.teal;
    const r = document.documentElement.style;
    r.setProperty("--ac", t.ac);
    r.setProperty("--ac-hover", t.hover);
    r.setProperty("--ac-soft", t.soft);
    r.setProperty("--ac-softfg", t.softfg);
    r.setProperty("--primary", t.ac);
    r.setProperty("--ring", t.ac);
  }, [idx]);

  if (active) {
    const section = SECTIONS.find((s) => s.value === active);
    const Comp = section.Comp;
    const withBackdrop = true;

    if (withBackdrop) {
      return (
        <div className="relative min-h-screen font-['Figtree']">
          <img src={backdrop} alt="" className="fixed inset-0 w-full h-full object-cover" />
          <div className="fixed inset-0 bg-stone-900/45" />
          <Navbar variant="transparent" onLogoClick={() => { setActive(null); setResumedSave(null); }} />
          <main className="relative z-10 max-w-6xl mx-auto mobile-page-gutter px-6 md:px-10 pt-24 pb-12">
            <BackButton tone="light" className="-ml-4 mb-3" onBack={() => { setActive(null); setResumedSave(null); }} />
            {location.state?.loginError && <button data-testid="login-return-error" onClick={openLogin} className="mb-4 w-full rounded-xl bg-white px-4 py-3 text-left text-sm text-rose-700">{location.state.loginError} Sign in</button>}
            <div data-testid={active === "packing" ? "packing-layout" : active === "saves" ? "saves-layout" : active === "bucket" ? "bucket-layout" : active === "itinerary" ? "itinerary-layout" : undefined} className={active === "itinerary" ? undefined : active === "packing" || active === "saves" || active === "bucket" ? "mobile-section-inset border border-transparent p-5 md:p-8" : "rounded-3xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-2xl p-5 md:p-8"}>
              <Comp key={accountEpoch} refreshKey={refreshKey} resumedSave={resumedSave} />
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-stone-50 font-['Figtree']">
        <Navbar variant="solid" onLogoClick={() => { setActive(null); setResumedSave(null); }} />
        <main className="max-w-6xl mx-auto mobile-page-gutter px-6 md:px-10 py-8 md:py-12">
          <Comp key={accountEpoch} refreshKey={refreshKey} resumedSave={resumedSave} />
        </main>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden font-['Figtree']">
      <motion.img
        key={backdrop}
        src={backdrop}
        alt="Today's destination"
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-stone-950/60 via-stone-950/40 to-stone-950/70" />

      <Navbar variant="transparent" onLogoClick={() => { setActive(null); setResumedSave(null); }} />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 max-w-6xl w-full mx-auto mobile-page-gutter px-6 md:px-10 flex flex-col justify-center pt-24 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="max-w-md"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Nunito'] text-white tracking-tight drop-shadow-sm">
              Hey {firstName}
            </h1>
            <p className="mt-2 text-lg sm:text-xl text-white/85 font-medium drop-shadow-sm">
              Where will you go next?
            </p>

            <div className="mt-8 flex flex-col gap-3">
              {SECTIONS.map((s, i) => (
                <motion.button
                  key={s.value}
                  data-testid={s.testid}
                  onClick={() => { setActive(s.value); setResumedSave(null); }}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 + i * 0.08 }}
                  className="group flex items-center gap-4 rounded-2xl bg-white/95 backdrop-blur-md border border-white/40 px-5 py-4 text-left shadow-lg shadow-stone-950/10 hover:bg-white hover:translate-x-1 transition-all duration-300"
                >
                  <span className={`w-11 h-11 rounded-xl grid place-items-center flex-shrink-0 ${s.c}`}>
                    <s.icon className="w-5 h-5" strokeWidth={2.5} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-bold font-['Nunito'] text-stone-900 text-lg leading-tight">{s.label}</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-ac group-hover:translate-x-0.5 transition-all" strokeWidth={2.5} />
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
        <footer data-testid="home-legal-footer" className="px-4 pb-5 text-white/85"><LegalLinks prefix="footer" newTab /></footer>
      </div>
    </div>
  );
}
